import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { venueUrl, experienceUrl } from '@/lib/urls';
import { motion } from 'framer-motion';
import { 
  Gift, MapPin, Star, Clock, ArrowLeft, Calendar,
  ChevronLeft, ChevronRight, Check, Minus, Plus, Building2,
  ShoppingBag, Info, AlertCircle, Play, Mail, ExternalLink,
  ShieldCheck, CreditCard, CalendarPlus, CalendarDays
} from 'lucide-react';
import { format, addDays } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';
import ConsumerLayout from '@/components/ConsumerLayout';
import { SocialShare } from '@/components/SocialShare';
import ContactOrganiserModal from '@/components/ContactOrganiserModal';
import { formatDate, formatTime } from '@/lib/dateUtils';
import api from '@/lib/api';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

// Gallery Section with auto-display and thumbnails
const GallerySection = ({ images, title }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  
  const placeholderImages = [
    'https://images.unsplash.com/photo-1642721085217-b5f5b6b47d0a?crop=entropy&cs=srgb&fm=jpg&q=85'
  ];
  
  const displayImages = images && images.length > 0 ? images : [];
  const thumbnails = displayImages.slice(0, 4);

  if (displayImages.length === 0) return null;

  return (
    <div className="space-y-4" id="section-gallery">
      <h3 className="text-lg font-bold text-foreground flex items-center gap-2">
        Gallery
      </h3>
      
      {/* Main Image Display */}
      <div className="relative aspect-video rounded-xl overflow-hidden group bg-gray-900">
        <img
          src={displayImages[currentIndex]}
          alt={`${title} - Image ${currentIndex + 1}`}
          className="w-full h-full object-cover"
        />
        
        {/* Play Button Overlay */}
        <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity">
          <button className="w-20 h-20 rounded-full bg-white/90 hover:bg-white flex items-center justify-center transition-all hover:scale-110">
            <Play className="w-8 h-8 text-purple-600 ml-1" fill="currentColor" />
          </button>
        </div>
        
        {/* Navigation Arrows */}
        {displayImages.length > 1 && (
          <>
            <button
              onClick={() => setCurrentIndex((prev) => (prev - 1 + displayImages.length) % displayImages.length)}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/60 hover:bg-black/80 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={() => setCurrentIndex((prev) => (prev + 1) % displayImages.length)}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/60 hover:bg-black/80 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </>
        )}
        
        {/* Experience Badge */}
        <div className="absolute top-4 left-4">
          <Badge className="bg-purple-600 text-white text-xs px-3 py-1">
            EVENT★GOERS EXPERIENCE
          </Badge>
        </div>
      </div>
      
      {/* Thumbnail Strip */}
      {displayImages.length > 1 && (
        <div className="flex gap-2">
          {thumbnails.map((img, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              className={`relative flex-1 aspect-video rounded-lg overflow-hidden border-2 transition-all ${
                idx === currentIndex ? 'border-purple-600 ring-2 ring-purple-600/30' : 'border-transparent hover:border-purple-400'
              }`}
            >
              <img src={img} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Location Section with Map
const LocationSection = ({ venue, experience }) => {
  const address = venue?.address || 'RiNo Art District, Denver, CO';
  const venueName = venue?.name || experience?.venue_name || 'Venue';
  
  const mapQuery = encodeURIComponent(`${venueName}, ${address}`);
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${mapQuery}`;
  
  // Embed URL for iframe
  const embedUrl = `https://www.google.com/maps/embed/v1/place?key=AIzaSyCHJPkfGCpXIT0225976P3tGS2rcL2xI_g&q=${mapQuery}`;
  
  return (
    <div className="space-y-4" id="section-location">
      <h3 className="text-lg font-bold text-foreground">Location</h3>
      
      {/* Map Embed */}
      <div className="relative aspect-video bg-gray-200 dark:bg-gray-800 rounded-xl overflow-hidden">
        <iframe
          src={embedUrl}
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Venue Location"
        />
        
        {/* Open in Maps Overlay Button */}
        <a
          href={mapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="absolute bottom-4 right-4 bg-white dark:bg-gray-900 shadow-lg rounded-lg px-4 py-2 flex items-center gap-2 hover:shadow-xl transition-shadow"
        >
          <MapPin className="w-4 h-4 text-purple-600" />
          <span className="font-medium text-sm">Open in Maps</span>
        </a>
      </div>
      
      {/* Venue Details */}
      <div className="space-y-2">
        <h4 className="font-bold text-foreground text-lg">{venueName}</h4>
        <p className="text-muted-foreground">{venue?.district || 'RiNo'}, Denver</p>
        {venue?.address && (
          <p className="text-sm text-muted-foreground">{venue.address}</p>
        )}
      </div>
    </div>
  );
};

// Organiser & Venue Cards
const OrganiserVenueCards = ({ venue, experience, navigate, user }) => {
  const [contactModalOpen, setContactModalOpen] = useState(false);

  return (
    <>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" id="section-organiser">
      {/* Organiser Card */}
      <Card>
        <CardContent className="p-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-3">Organiser</p>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-purple-600 flex items-center justify-center text-white font-bold">
              {venue?.name?.charAt(0) || experience?.venue_name?.charAt(0) || 'O'}
            </div>
            <div>
              <h4 className="font-bold text-foreground">{venue?.name || experience?.venue_name}</h4>
              <button 
                onClick={() => {
                  console.log('Contact Organiser button clicked');
                  setContactModalOpen(true);
                }}
                className="text-purple-600 hover:text-purple-700 text-sm font-medium flex items-center gap-1 mt-1"
                data-testid="contact-organiser-btn"
                type="button"
              >
                <Mail className="w-3 h-3" />
                Contact Organiser
              </button>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Venue Card */}
      <Card id="section-venue">
        <CardContent className="p-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-3">Venue</p>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
              <Building2 className="w-6 h-6 text-gray-500" />
            </div>
            <div>
              {venue?.id ? (
                <>
                  <Link 
                    to={venueUrl(venue)}
                    className="font-bold text-foreground hover:text-purple-600 transition-colors block"
                    data-testid="view-venue-profile-link"
                  >
                    View Venue Profile
                  </Link>
                  <Link 
                    to={venueUrl(venue)}
                    className="text-purple-600 hover:text-purple-700 text-sm font-medium flex items-center gap-1 mt-1"
                    data-testid="see-all-experiences-link"
                  >
                    See all experiences
                    <ExternalLink className="w-3 h-3" />
                  </Link>
                </>
              ) : (
                <>
                  <span className="font-bold text-muted-foreground block">
                    {experience?.venue_name || 'Venue'}
                  </span>
                  <span className="text-muted-foreground text-sm">
                    Venue details coming soon
                  </span>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
    
    {/* Contact Organiser Modal */}
    <ContactOrganiserModal
      isOpen={contactModalOpen}
      onClose={() => setContactModalOpen(false)}
      eventTitle={experience?.title || 'Experience'}
      eventId={experience?.id}
      organiserId={experience?.created_by || venue?.partner_id}
      organiserName={venue?.name || experience?.venue_name || 'Organiser'}
      userEmail={user?.email || ''}
      userName={user?.full_name || ''}
    />
    </>
  );
};

// Experience Booking Widget - Sticky
const ExperienceBookingWidget = ({ experience, isGold, isAuthenticated, venue }) => {
  const minTickets = experience.min_tickets || 1;
  const [quantity, setQuantity] = useState(minTickets);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  
  const displayPrice = isGold && experience.gold_price ? experience.gold_price : experience.price;
  const originalPrice = experience.price;
  const hasSavings = isGold && experience.gold_price && experience.gold_price < experience.price;
  const totalPrice = displayPrice * quantity;
  
  const requiresReservation = experience.requires_reservation;
  const flexibleTime = experience.flexible_time !== false;
  const timeSlots = experience.time_slots || [];
  
  // Parse valid_until date
  const validUntilDate = experience.valid_until ? new Date(experience.valid_until) : addDays(new Date(), 90);
  
  // Check if date is blacked out
  const isDateBlackedOut = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    
    // Check individual blackout dates
    if (experience.blackout_dates?.includes(dateStr)) return true;
    
    // Check blackout ranges
    for (const range of experience.blackout_ranges || []) {
      if (range.start && range.end && dateStr >= range.start && dateStr <= range.end) {
        return true;
      }
    }
    
    // Check recurring blackouts (day of week)
    const dayName = format(date, 'EEEE').toLowerCase();
    if (experience.recurring_blackouts?.includes(dayName)) return true;
    
    return false;
  };

  const handlePurchase = async () => {
    // Validate reservation if required
    if (requiresReservation && !selectedDate) {
      toast.error('Please select a date for your experience');
      return;
    }
    
    if (requiresReservation && !flexibleTime && !selectedTime) {
      toast.error('Please select a time slot');
      return;
    }
    
    // Check advance notice
    if (requiresReservation && selectedDate && experience.advance_notice_hours > 0) {
      const hoursUntil = (selectedDate - new Date()) / (1000 * 60 * 60);
      if (hoursUntil < experience.advance_notice_hours) {
        toast.error(`Reservations require at least ${experience.advance_notice_hours} hours advance notice`);
        return;
      }
    }

    setLoading(true);
    try {
      const response = await api.post('/payments/checkout', {
        item_type: 'voucher',
        item_id: experience.id,
        quantity: quantity,
        origin_url: window.location.origin,
        reservation_date: selectedDate ? format(selectedDate, 'yyyy-MM-dd') : null,
        reservation_time: selectedTime
      });
      
      window.location.href = response.data.url;
    } catch (error) {
      if (error.response?.status === 401) {
        toast.info('Please sign in to complete your purchase');
        navigate(`/auth?redirect=${encodeURIComponent(window.location.pathname)}`);
      } else {
        toast.error(error.response?.data?.detail || 'Purchase failed');
      }
      setLoading(false);
    }
  };

  return (
    <Card className="border-2 shadow-xl overflow-hidden" data-testid="experience-booking-widget">
      {/* Header with Price */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-6 text-center">
        <p className="text-sm uppercase tracking-wider opacity-90 mb-1">Experience From</p>
        <div className="flex items-baseline justify-center gap-2">
          <span className="text-5xl font-bold">${displayPrice.toFixed(0)}</span>
          {hasSavings && (
            <span className="text-lg opacity-75 line-through">${originalPrice.toFixed(0)}</span>
          )}
        </div>
        {hasSavings && (
          <Badge className="mt-2 bg-yellow-400 text-yellow-900">
            <Star className="w-3 h-3 mr-1 fill-current" />
            GOLD MEMBER PRICE
          </Badge>
        )}
      </div>
      
      <CardContent className="p-6 space-y-6">
        {/* Valid Until */}
        <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <Clock className="w-5 h-5 text-purple-600" />
          <div>
            <p className="font-semibold text-foreground">Valid Until</p>
            <p className="text-sm text-muted-foreground">{formatDate(experience.valid_until)}</p>
          </div>
        </div>
        
        {/* Reservation Section (if required) */}
        {requiresReservation && (
          <div className="space-y-4">
            <h4 className="font-bold text-foreground uppercase text-sm tracking-wider">
              Book Your Date
            </h4>
            
            {/* Date Picker */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">Select Date *</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !selectedDate && "text-muted-foreground"
                    )}
                    data-testid="reservation-date-picker"
                  >
                    <CalendarDays className="w-4 h-4 mr-2" />
                    {selectedDate ? formatDate(format(selectedDate, 'yyyy-MM-dd')) : 'Choose a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarComponent
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    disabled={(date) => 
                      date < new Date() || 
                      date > validUntilDate ||
                      isDateBlackedOut(date)
                    }
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            
            {/* Time Slot Picker (if not flexible) */}
            {!flexibleTime && timeSlots.length > 0 && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-muted-foreground">Select Time *</label>
                <Select value={selectedTime} onValueChange={setSelectedTime}>
                  <SelectTrigger data-testid="reservation-time-picker">
                    <SelectValue placeholder="Choose a time slot" />
                  </SelectTrigger>
                  <SelectContent>
                    {timeSlots.map((slot) => (
                      <SelectItem key={slot} value={slot}>
                        {formatTime(slot)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Advance Notice Info */}
            {experience.advance_notice_hours > 0 && (
              <p className="text-xs text-amber-600 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                Requires {experience.advance_notice_hours}h advance notice
              </p>
            )}
          </div>
        )}

        {/* Quantity Selector */}
        <div className="space-y-3">
          <label className="font-bold text-foreground uppercase text-sm tracking-wider">
            Quantity
          </label>
          <div className="flex items-center justify-center gap-4">
            <Button
              variant="outline"
              size="icon"
              onClick={() => setQuantity(Math.max(minTickets, quantity - 1))}
              disabled={quantity <= 1}
              data-testid="decrease-quantity"
            >
              <Minus className="w-4 h-4" />
            </Button>
            <span className="text-3xl font-bold w-16 text-center" data-testid="experience-quantity">
              {quantity}
            </span>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setQuantity(Math.min(10, quantity + 1))}
              disabled={quantity >= 10}
              data-testid="increase-quantity"
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          {minTickets > 1 && (
            <p className="text-xs text-amber-600 mt-1 font-medium">Minimum {minTickets} tickets required</p>
          )}
        </div>

        {/* Total */}
        <div className="bg-purple-50 dark:bg-purple-950/30 rounded-lg p-4">
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">{quantity} experience{quantity > 1 ? 's' : ''}</span>
            <span className="text-2xl font-bold text-purple-600">${totalPrice.toFixed(2)}</span>
          </div>
        </div>

        {/* Purchase Button */}
        <Button
          className="w-full h-14 bg-green-600 hover:bg-green-700 text-white font-bold text-lg"
          onClick={handlePurchase}
          disabled={loading}
          data-testid="buy-experience-btn"
        >
          {loading ? (
            <span className="flex items-center gap-2">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
              Processing...
            </span>
          ) : (
            <>
              <ShoppingBag className="w-5 h-5 mr-2" />
              Buy Experience
            </>
          )}
        </Button>

        {/* Trust Badges */}
        <div className="space-y-3 pt-2">
          <div className="flex items-center gap-2 text-sm text-green-600">
            <Check className="w-4 h-4" />
            <span>Instant Confirmation via Email</span>
          </div>
          
          <div className="border-t pt-3">
            <p className="text-xs text-muted-foreground text-center mb-2 uppercase tracking-wider">
              Safe & Secure Payment
            </p>
            <div className="flex items-center justify-center gap-3 opacity-60">
              <CreditCard className="w-8 h-8" />
              <ShieldCheck className="w-6 h-6" />
            </div>
          </div>
        </div>

        <p className="text-xs text-center text-muted-foreground">
          Secure checkout powered by Eventgoers
        </p>
      </CardContent>
    </Card>
  );
};

const ExperienceDetail = () => {
  const { id, idOrState, customUrl, state, city, neighborhood, slug, venueSlug } = useParams();
  const effectiveId = id || (idOrState && /^[0-9a-f]{8}-/i.test(idOrState) ? idOrState : null);
  const navigate = useNavigate();
  const { isAuthenticated, isGold, user } = useAuth();
  const [experience, setExperience] = useState(null);
  const [venue, setVenue] = useState(null);
  const [relatedExperiences, setRelatedExperiences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('gallery');

  useEffect(() => {
    fetchExperience();
  }, [effectiveId, customUrl, slug]);

  const fetchExperience = async () => {
    try {
      let response;
      
      if (slug && state && city && neighborhood) {
        const venueSlugPart = venueSlug ? `${venueSlug}/` : '';
        response = await api.get(`/vouchers/by-slug/${state}/${city}/${neighborhood}/${venueSlugPart}${slug}`);
      } else if (customUrl) {
        response = await api.get(`/vouchers/by-url/${customUrl}`);
      } else {
        response = await api.get(`/vouchers/${effectiveId}`);
      }
      
      setExperience(response.data);
      
      // Fetch venue details
      if (response.data.venue_id) {
        try {
          const venueResponse = await api.get(`/venues/${response.data.venue_id}`);
          setVenue(venueResponse.data);
        } catch (e) {
          console.log('Venue not found');
        }
      }
      
      // Fetch related experiences
      try {
        const expId = response.data.id || id;
        const relatedResponse = await api.get(`/vouchers/${expId}/related`);
        setRelatedExperiences(relatedResponse.data);
      } catch (e) {
        console.log('No related experiences');
      }
    } catch (error) {
      toast.error('Failed to load experience');
    } finally {
      setLoading(false);
    }
  };

  // Navigation tabs with section IDs
  const navTabs = [
    { id: 'gallery', label: 'Gallery', sectionId: 'section-gallery' },
    { id: 'description', label: 'Description', sectionId: 'section-description' },
    { id: 'included', label: "What's Included", sectionId: 'section-included' },
    { id: 'location', label: 'Location', sectionId: 'section-location' },
    { id: 'organiser', label: 'Organiser', sectionId: 'section-organiser' },
    { id: 'venue', label: 'Venue', sectionId: 'section-venue' },
    { id: 'terms', label: 'Terms', sectionId: 'section-terms' },
  ];
  
  // Scroll to section when tab is clicked
  const handleTabClick = (tab) => {
    setActiveSection(tab.id);
    const element = document.getElementById(tab.sectionId);
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  if (loading) {
    return (
      <ConsumerLayout hideFooter>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600" />
        </div>
      </ConsumerLayout>
    );
  }

  if (!experience) {
    return (
      <ConsumerLayout hideFooter>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <h1 className="text-3xl font-bold mb-4">Experience not found</h1>
          <Button onClick={() => navigate('/marketplace')} data-testid="back-to-marketplace-btn">
            Back to Marketplace
          </Button>
        </div>
      </ConsumerLayout>
    );
  }

  const whatsIncluded = experience.whats_included || [
    'Full experience as described',
    'All taxes and fees included',
    'Flexible redemption dates'
  ];

  const terms = experience.terms || [
    'Valid for single use only',
    'Non-refundable after purchase',
    'Must be redeemed before expiry date',
    'Subject to availability'
  ];

  return (
    <ConsumerLayout>

      {/* Hero Section */}
      <div className="bg-gray-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button
            variant="ghost"
            className="mb-4 text-white/80 hover:text-white hover:bg-white/10"
            onClick={() => navigate(-1)}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          {/* Badges */}
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <Badge className="bg-purple-600 text-white border-0">
              <Gift className="w-3 h-3 mr-1" />
              EXPERIENCE
            </Badge>
            {experience.gold_price && (
              <Badge className="bg-[#D4AF37] text-black border-0">
                <Star className="w-3 h-3 mr-1 fill-current" />
                GOLD DEAL
              </Badge>
            )}
            {experience.requires_reservation && (
              <Badge className="bg-amber-500 text-white border-0">
                <Calendar className="w-3 h-3 mr-1" />
                RESERVATION REQUIRED
              </Badge>
            )}
          </div>
          
          {/* Title */}
          <h1 className="text-3xl lg:text-4xl font-bold mb-2" data-testid="experience-title">
            {experience.title}
          </h1>
          
          {/* The Hook */}
          {experience.hook && (
            <p className="text-lg text-white/80 mb-4 italic">{experience.hook}</p>
          )}
          
          {/* Quick Info & Social Share */}
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-6 text-white/90">
              {venue?.id ? (
                <Link 
                  to={venueUrl(venue)}
                  className="flex items-center gap-2 hover:text-white transition-colors"
                  data-testid="venue-link"
                >
                  <MapPin className="w-5 h-5" />
                  <span className="underline">{experience.venue_name}</span>
                </Link>
              ) : (
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  <span>{experience.venue_name}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                <span>Valid until {formatDate(experience.valid_until)}</span>
              </div>
            </div>
            
            <SocialShare title={experience.title} description={experience.description} />
          </div>
        </div>
      </div>

      {/* Section Navigation */}
      <div className="border-b sticky top-0 bg-background z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex gap-1 overflow-x-auto py-2">
            {navTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabClick(tab)}
                className={`px-4 py-2 text-sm font-medium whitespace-nowrap rounded-lg transition-colors ${
                  activeSection === tab.id
                    ? 'bg-purple-100 dark:bg-purple-950 text-purple-600'
                    : 'text-muted-foreground hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
                data-testid={`nav-tab-${tab.id}`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Gallery Section */}
            <GallerySection images={experience.images} title={experience.title} />
            
            {/* About This Experience */}
            <div className="space-y-4" id="section-description">
              {experience.experience_description ? (
                <>
                  <h3 className="text-lg font-bold text-foreground">The Experience</h3>
                  <p className="text-muted-foreground leading-relaxed">{experience.experience_description}</p>
                </>
              ) : (
                <>
                  <h3 className="text-lg font-bold text-foreground">About This Experience</h3>
                  <p className="text-muted-foreground leading-relaxed">{experience.description}</p>
                </>
              )}
              
              {/* The Vibe Tags */}
              {experience.vibe_tags?.length > 0 && (
                <div className="pt-2">
                  <p className="text-sm font-semibold text-muted-foreground mb-2">The Vibe</p>
                  <div className="flex flex-wrap gap-2">
                    {experience.vibe_tags.map(tag => (
                      <Link key={tag} to={`/experiences/vibe/${encodeURIComponent(tag.toLowerCase().replace(/\s+/g, '-'))}`}>
                        <Badge variant="secondary" className="text-sm cursor-pointer hover:bg-purple-100 hover:text-purple-700 transition-colors">{tag}</Badge>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Exclusive badges */}
              <div className="flex flex-wrap gap-2 pt-2">
                {experience.is_date_night && <Link to="/experiences/vibe/date-night"><Badge className="bg-pink-100 text-pink-700 border-pink-200 cursor-pointer hover:bg-pink-200">Date Night</Badge></Link>}
                {experience.is_date_night_exclusive && <Badge className="bg-pink-600 text-white">Date Night Exclusive</Badge>}
                {experience.is_eventgoers_exclusive && <Link to="/experiences/vibe/eventgoers-exclusive"><Badge className="bg-indigo-600 text-white cursor-pointer hover:bg-indigo-700">EVENT★GOERS Exclusive</Badge></Link>}
              </div>
            </div>

            {/* What's Included */}
            <Card id="section-included">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-500" />
                  What's Included
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {whatsIncluded.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
            
            {/* Location Section */}
            <LocationSection venue={venue} experience={experience} />
            
            {/* Organiser & Venue Cards */}
            <OrganiserVenueCards venue={venue} experience={experience} navigate={navigate} user={user} />

            {/* Terms & Conditions */}
            <Card id="section-terms">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Info className="w-5 h-5 text-muted-foreground" />
                  Terms & Conditions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  {terms.map((term, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <span className="text-muted-foreground">•</span>
                      <span>{term}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Related Experiences */}
            {relatedExperiences.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-lg font-bold text-foreground">More From This Venue</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {relatedExperiences.slice(0, 4).map((relExp) => (
                    <Link
                      key={relExp.id}
                      to={experienceUrl(relExp)}
                      className="flex items-center gap-3 p-3 border rounded-lg hover:border-purple-600/50 transition-colors group"
                    >
                      <div className="w-16 h-16 rounded-lg overflow-hidden bg-gradient-to-br from-purple-100 to-pink-100 flex-shrink-0">
                        {relExp.images?.[0] ? (
                          <img src={relExp.images[0]} alt={relExp.title} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Gift className="w-6 h-6 text-purple-400" />
                          </div>
                        )}
                      </div>
                      <div className="flex-grow min-w-0">
                        <h4 className="font-medium text-foreground group-hover:text-purple-600 transition-colors truncate">
                          {relExp.title}
                        </h4>
                        <p className="text-sm text-muted-foreground">Valid until {formatDate(relExp.valid_until)}</p>
                        <p className="text-sm font-bold">${relExp.price}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Sticky Booking Widget */}
          <div className="lg:col-span-1">
            <div className="sticky top-20">
              <ExperienceBookingWidget 
                experience={experience}
                isGold={isGold}
                isAuthenticated={isAuthenticated}
                venue={venue}
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* Footer */}
    </ConsumerLayout>
  );
};

export default ExperienceDetail;
